import {
  AlertEntity,
  AlertAndDetectionTenantInfo,
} from '@investigator/shared/generated/graphql';
import { DetectionSummaryInsights } from '../../features/entities/types/entityTable';
import {
  LocalDetection,
  LocalDetectionSummary,
} from '@investigator/features/detections/queries/types';

/**
 * Gets a standardized DetectionSummary object from various sources.
 * This ensures consistency across all tables and components, preventing
 * stale state issues with entity suppression and other data.
 *
 * @param source - Either a DetectionSummary or Detection object
 * @param entityOverride - Optional entity data to override the source entity
 * @returns A normalized DetectionSummary object
 *
 * @example
 * // Create from Detection object
 * const summary = getDetectionSummary(detection);
 *
 * @example
 * // Create from Detection with entity override (for multi-entity scenarios)
 * const summary = getDetectionSummary(detection, sourceEntity);
 */
export const getDetectionSummary = (
  source: LocalDetectionSummary | LocalDetection,
  entityOverride?: Partial<AlertEntity>,
): LocalDetectionSummary => {
  const isDetection = 'detection_id' in source && 'alert_info' in source;

  if (isDetection) {
    // Create from Detection object
    const detection = source as LocalDetection;
    const entity = { ...detection.alert_entity, ...entityOverride };

    return {
      detection_id: detection.detection_id,
      entity_id: entity.entity_id ?? '',
      entity_name: entity.entity_name ?? '',
      entity_type: entity.entity_type ?? '',
      entity_category:
        entity.entity_category === null ? undefined : entity.entity_category,
      detection_count: detection.total_alert_count ?? 0,
      total_detections: 1,
      severity: detection.rank?.severity ?? 0,
      first_seen: detection.earliest_start_timestamp ?? 0,
      last_seen: detection.latest_start_timestamp ?? 0,
      tenant_entity: detection.detection_keys?.tenant_entity ?? '',
      tenant_detection: detection.detection_keys?.tenant_detection ?? undefined,
      tenant_info: detection.tenant_info ?? ({} as AlertAndDetectionTenantInfo),
      alert_name: detection.alert_info?.alert_name ?? undefined,
      alert_type: detection.alert_info?.alert_type ?? undefined,
      content_id: detection.alert_info?.content_id ?? undefined,
      tenant: detection.tenant_info?.tenant_id ?? undefined,
      agentic_triage_report_id: detection.agentic_triage_report_id ?? undefined,
    } as LocalDetectionSummary;
  }

  // Already a DetectionSummary, just normalize nulls to undefined
  const summary = source as LocalDetectionSummary;
  return {
    ...summary,
    entity_category:
      summary.entity_category === null ? undefined : summary.entity_category,
    alert_name: summary.alert_name ?? undefined,
    alert_type: summary.alert_type ?? undefined,
    content_id: summary.content_id ?? undefined,
    tenant: summary.tenant ?? summary.tenant_info?.tenant_id ?? undefined,
  } as LocalDetectionSummary;
};

/**
 * Creates a unique cache key for entity suppression status.
 * Use this to ensure consistent cache keys across the app.
 *
 * @param entityName - The entity name
 * @param entityType - The entity type
 * @param contentId - Optional content ID
 * @param tenantId - Optional tenant ID
 * @returns A unique string key for cache identification
 *
 * @example
 * const key = getEntitySuppressionCacheKey('192.168.1.1', 'IP', 'content123', 'tenant456');
 * // Returns: 'entity-suppression:tenant456:content123:IP:192.168.1.1'
 */
export const getEntitySuppressionCacheKey = (
  entityName: string,
  entityType: string,
  contentId?: string,
  tenantId?: string,
): string => {
  return `entity-suppression:${tenantId ?? 'unknown'}:${contentId ?? 'unknown'}:${entityType}:${entityName}`;
};

/**
 * Converts a DetectionSummaryInsights (from the insights API) to a DetectionSummary
 * for compatibility with components that expect the older format.
 *
 * Note: Some fields like content_id, alert_name, alert_type are not available
 * in the insights API and will be undefined. These are populated later by
 * useEntityAnalysisDetailsQueries when the entity is selected.
 *
 * @param insight - A DetectionSummaryInsights from getTopDetectionEntitiesInsightsPaginated
 * @returns A normalized DetectionSummary object
 *
 * @example
 * const summary = getInsightDetectionSummary(insight);
 */
export const getInsightDetectionSummary = (
  insight: DetectionSummaryInsights,
): LocalDetectionSummary => {
  return {
    entity_id: insight.entity_id ?? '',
    entity_name: insight.entity_name ?? '',
    entity_type: insight.entity_type ?? '',
    entity_category:
      (insight.entity_category as 'source' | 'destination') ?? undefined,
    tenant: insight.tenant ?? undefined,
    tenant_entity: insight.tenant_entity ?? '',
    tenant_info: {
      tenant_id: insight.tenant ?? '',
      tenant_display_name: insight.tenant ?? '',
    },
    // Use max_severity as severity
    severity: insight.max_severity ?? 0,
    // Use total_detections for counts
    detection_count: insight.total_detections ?? 0,
    total_detections: insight.total_detections ?? 0,
    // Map agentic triage report ID from ai_insights
    agentic_triage_report_id:
      insight.ai_insights?.ai_insight_report_id ?? undefined,
    // These fields aren't available in insights API - will be fetched by timeline query
    content_id: undefined,
    alert_name: undefined,
    alert_type: undefined,
    detection_id: undefined,
    // Default timestamps (not available in insights API)
    first_seen: 0,
    last_seen: 0,
  };
};
